import { createRoute, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { useCartStore } from '../store';
import { Trash2, Minus, Plus, ShoppingBag, Store } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cart',
  component: Cart,
});

function Cart() {
  const { items, removeItem, updateQuantity, total } = useCartStore();

  const cartStructuredData = items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Shopping Cart",
    "numberOfItems": items.length,
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Product",
        "name": item.name,
        "offers": {
          "@type": "Offer",
          "price": (item.price / 100).toFixed(2),
          "priceCurrency": "CAD"
        }
      }
    }))
  } : null;

  const cartTitle = `Your Cart (${items.reduce((sum, item) => sum + item.quantity, 0)} items) - Cardston Market`;

  // Group items by vendor
  const itemsByVendor = items.reduce((acc, item) => {
    const vendorId = item.vendorId || 'unknown';
    if (!acc[vendorId]) {
      acc[vendorId] = {
        vendorName: item.vendorName || 'Local Vendor',
        items: []
      };
    }
    acc[vendorId].items.push(item);
    return acc;
  }, {} as Record<string, { vendorName: string, items: typeof items }>);

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32 max-w-2xl mx-auto text-center">
      <Helmet>
        <title>Your Cart - Cardston Market</title>
        <meta name="description" content="Review your shopping cart on Cardston Market. Proceed to secure checkout when you're ready." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
        <div className="w-24 h-24 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-8">
          <ShoppingBag className="w-10 h-10 text-neutral-400 dark:text-neutral-500 stroke-[1.5]" />
        </div>
        <h2 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white mb-4 tracking-tight">Your cart is empty</h2>
        <p className="text-neutral-500 dark:text-neutral-400 mb-10 text-lg font-light">Looks like you haven't added anything yet.</p>
        <Link
          to="/"
          className="bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-8 py-4 rounded-full text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shadow-sm"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto w-full py-12 px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>{cartTitle}</title>
          <meta name="description" content={`Your cart has ${items.length} item${items.length !== 1 ? 's' : ''} totaling $${(total / 100).toFixed(2)}. Proceed to checkout on Cardston Market.`} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      <h1 className="text-4xl font-serif font-medium text-neutral-900 dark:text-white mb-12 tracking-tight">Shopping Cart</h1>

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
        {Object.entries(itemsByVendor).map(([vendorId, vendorGroup]) => (
          <div key={vendorId} className="border-b border-neutral-200 dark:border-neutral-800 last:border-b-0">
            <div className="bg-neutral-50 dark:bg-neutral-800/50 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-2 text-neutral-900 dark:text-white font-medium">
                <Store className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                {vendorGroup.vendorName}
              </div>
              {vendorId !== 'unknown' && (
                <Link to="/shop/$vendorId" params={{ vendorId }} className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:underline transition-colors">
                  Visit Store
                </Link>
              )}
            </div>
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {vendorGroup.items.map((item) => (
                <li key={item.id} className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-8 group">
                  <div className="w-32 h-40 shrink-0 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden relative">
                    <img
                      src={item.imageUrl || 'https://picsum.photos/seed/product/200/300'}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="flex-1 flex flex-col justify-between w-full h-full min-h-[10rem] py-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{item.name}</h3>
                        <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-4 line-clamp-1">{item.description || 'Premium essential'}</p>
                      </div>
                      <p className="text-lg font-medium text-neutral-900 dark:text-white">${(item.price / 100).toFixed(2)}</p>
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center gap-4 bg-neutral-50 dark:bg-neutral-800 rounded-full border border-neutral-200 dark:border-neutral-700 p-1">
                        <button
                          onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                          className="p-1.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-white dark:hover:bg-neutral-700 rounded-full transition-all disabled:opacity-50"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-6 text-center text-sm font-medium text-neutral-900 dark:text-white">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-white dark:hover:bg-neutral-700 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={item.quantity >= (item.stock !== undefined ? item.stock : (item.maxOrderQuantity !== undefined ? item.maxOrderQuantity : 5))}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-sm font-medium text-neutral-400 dark:text-neutral-500 hover:text-red-600 dark:hover:text-red-400 transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Remove</span>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="bg-neutral-50 dark:bg-neutral-800/80 p-6 sm:p-8 border-t border-neutral-200 dark:border-neutral-800">
          <div className="flex justify-between items-end mb-8">
            <div>
              <span className="block text-sm text-neutral-500 dark:text-neutral-400 mb-1">Subtotal</span>
              <span className="block text-sm text-neutral-400 dark:text-neutral-500">Shipping & taxes calculated at checkout</span>
            </div>
            <span className="text-3xl font-serif font-medium text-neutral-900 dark:text-white">${(total / 100).toFixed(2)}</span>
          </div>
          
          <Link
            to="/checkout"
            className="w-full flex justify-center items-center py-4 px-8 rounded-full text-sm font-medium text-white bg-neutral-900 dark:bg-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shadow-sm"
          >
            Proceed to Checkout
          </Link>
        </div>
      </div>
    </div>
  );
}
